import React, { useContext } from 'react';
import { Redirect, Route, RouteProps } from 'react-router-dom';
import { Box, CircularProgress } from '@material-ui/core';
import { AuthContext, EnumUserRole } from '../../AuthProvider';
import { logger } from '../../../utils/logger';

interface Props extends RouteProps {
  roles: EnumUserRole[];
}

const ProtectedRoleRoute = ({ roles = [], ...rest }: Props): React.ReactElement => {
  const authContext = useContext(AuthContext);
  if (authContext === null) {
    throw new Error('No AuthContext');
  }

  const { userRole, userId } = authContext;

  // Show loading spinner while userRole is being determined
  // This prevents redirect loops and blank screens during auth initialization
  if (userRole === null) {
    logger.info('ProtectedRoleRoute: Waiting for user role to load', {
      route: window.location.pathname,
      userId: userId || undefined,
    });

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if user has the required role
  if (roles.includes(userRole as EnumUserRole)) {
    return <Route {...rest} />;
  }

  // User is authenticated but doesn't have the required role
  logger.warn(`ProtectedRoleRoute: User lacks required role (${roles.join(', ')})`, {
    route: window.location.pathname,
    userRole,
  });

  return <Redirect to="/not-authorised" />;
};

export default ProtectedRoleRoute;
